import { CustomModal } from 'components/molecules/Modal/Modal'
import dayjs from 'lib/dayjs'
import { IconButton, Typography, Button } from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import PrintIcon from '@mui/icons-material/Print'
import { ImplantReportStatus } from 'common/types'
import { useViewImplantReportModalLogic } from './ViewImplantReportModal.logic'
import '../ImplantReportSender.scss'
import { ViewImplantReportModalProps } from '../ImplantReportSender.types'
import { useState } from 'react'
import { convertHtmlToPdf } from 'lib/services/restApi/pdfService'

export const ViewImplantReportModal = ({
  implantReportStatus,
  formatEnumValue,
  viewReportsModalOpen,
  setViewReportsModalOpen,
}: ViewImplantReportModalProps) => {
  const [isPrinting, setIsPrinting] = useState(false)

  const {
    isMobile,
    currentReport,
    currentReportState,
    currentReportIndex,
    handleAttchementClick,
    handleNextReport,
    getAttachmentLink,
    handlePreviousReport,
  } = useViewImplantReportModalLogic(implantReportStatus)

  const handlePrint = async () => {
    try {
      setIsPrinting(true)
      if (
        !currentReport.implantReportPrintable &&
        !currentReport.implantReport
      ) {
        throw new Error('No printable content available')
      }
      const pdfBuffer = await convertHtmlToPdf(
        currentReport.implantReportPrintable ||
          currentReport.implantReport ||
          ''
      )

      const binaryString = Buffer.from(pdfBuffer, 'base64').toString('binary')
      const bytes = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      const blob = new Blob([bytes], { type: 'application/pdf' })

      const url = URL.createObjectURL(blob)

      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      document.body.appendChild(iframe)

      iframe.src = url

      const checkIframeLoaded = setInterval(() => {
        if (iframe.contentDocument?.readyState === 'complete') {
          clearInterval(checkIframeLoaded)

          const contentWindow = iframe.contentWindow
          if (contentWindow) {
            contentWindow.onafterprint = () => {
              document.body.removeChild(iframe)
              URL.revokeObjectURL(url)
              setIsPrinting(false)
            }

            contentWindow.onclose = () => {
              document.body.removeChild(iframe)
              URL.revokeObjectURL(url)
              setIsPrinting(false)
            }

            window.addEventListener('focus', function onFocus() {
              window.removeEventListener('focus', onFocus)
              setTimeout(() => {
                document.body.removeChild(iframe)
                URL.revokeObjectURL(url)
                setIsPrinting(false)
              }, 100)
            })

            contentWindow.print()
          }
        }
      }, 100)
    } catch (error) {
      console.error('Error converting HTML to PDF:', error)
      setIsPrinting(false)
    }
  }

  return (
    <CustomModal
      open={viewReportsModalOpen}
      closeButtonOnly
      header="Sent Report(s)"
      isLoadingState={false}
      secondaryButtonAction={() => setViewReportsModalOpen(false)}
      subheader={`${
        currentReport.type && formatEnumValue(currentReport.type)
      } Report sent to Dr. ${currentReport.referringPhysicianLastName} - ${
        currentReport.referringPhysicianEmail
      } at ${dayjs(currentReport.createdAt).format('hh:mm A M-D-YY')}`}
      subSubHeader={
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '16px',
            }}
          >
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={isPrinting}
            >
              {isPrinting ? 'Generating PDF...' : 'Print Report'}
            </Button>
          </div>
          {currentReport?.attachments &&
            currentReport.attachments.length > 0 && (
              <div className="attachement-container-reports">
                <Typography
                  variant="h6"
                  className={`attachment-title ${isMobile ? 'isMobile' : ''}`}
                >
                  Attachments:
                </Typography>
                <div className="attachment-container">
                  {currentReportState?.attachments &&
                    currentReportState.attachments.map(
                      ({ filename, bucketKey, pdfThumbnail }, index) => {
                        const isPDF = filename
                          .toLocaleLowerCase()
                          .endsWith('.pdf')
                        const attachmentLink = getAttachmentLink(bucketKey)

                        return (
                          <div className="attachment-box" key={index}>
                            <img
                              onClick={handleAttchementClick(bucketKey)}
                              className="attachment-image"
                              src={isPDF ? pdfThumbnail : attachmentLink}
                              alt=""
                            />
                          </div>
                        )
                      }
                    )}
                </div>
              </div>
            )}
          <div className="flex-end">
            <IconButton
              onClick={handlePreviousReport}
              disabled={
                (implantReportStatus as ImplantReportStatus[]).length <= 1 ||
                currentReportIndex === 0
              }
              color={'primary'}
            >
              <ArrowBackIosIcon />
            </IconButton>
            <IconButton
              onClick={handleNextReport}
              disabled={
                (implantReportStatus as ImplantReportStatus[]).length <= 1 ||
                currentReportIndex ===
                  (implantReportStatus as ImplantReportStatus[]).length - 1
              }
              color={'primary'}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </div>
        </div>
      }
    >
      <div
        className="view-reports-modal"
        dangerouslySetInnerHTML={{
          __html: currentReport.implantReport as string,
        }}
      />
    </CustomModal>
  )
}
